/**
 * LandingPage
 * -----------
 * Public marketing hero for the Rage Room Scheduler. Highlights the
 * booking experience and funnels visitors directly into the /book flow.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import useApi from '../hooks/useApi';
import Footer from '../components/Footer';

const REMAINING_CAPACITY_FIELD_ORDER = [
  'totalRemainingCapacity',
  'remainingCapacity',
  'availableSlotQuantity',
  'availableSlots',
  'slotQuantityAvailable',
  'slotRemaining',
  'sessionCapacity',
  'capacity',
];

const TOTAL_CAPACITY_FIELD_ORDER = [
  'totalCapacity',
  'maxCapacity',
  'sessionCapacity',
  'capacity',
];

const coerceNonNegativeNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric) || numeric < 0) {
    return null;
  }
  return numeric;
};

const resolveCapacityFromFields = (row, fieldNames) => {
  if (!row || !Array.isArray(fieldNames) || fieldNames.length === 0) {
    return 0;
  }
  for (const fieldName of fieldNames) {
    const candidate = coerceNonNegativeNumber(row[fieldName]);
    if (candidate !== null) {
      return candidate;
    }
  }
  return 0;
};

const summarizeSessionCapacity = (sessionRows = []) =>
  sessionRows.reduce(
    (acc, row) => {
      const remaining = resolveCapacityFromFields(row, REMAINING_CAPACITY_FIELD_ORDER);
      const capacityCandidate = resolveCapacityFromFields(row, TOTAL_CAPACITY_FIELD_ORDER);
      const normalizedCapacity =
        capacityCandidate > 0 ? Math.max(capacityCandidate, remaining) : remaining;
      acc.totalRemaining += remaining;
      acc.totalCapacity += normalizedCapacity;
      return acc;
    },
    { totalRemaining: 0, totalCapacity: 0 }
  );

const resolveRoomIdentifier = (row = {}) => {
  const candidate =
    row.roomId ??
    row.room?.id ??
    row.room?._id ??
    row.room?.roomId ??
    row.room?.uuid ??
    row.roomUuid ??
    null;

  if (candidate === null || candidate === undefined) {
    return '';
  }

  return String(candidate);
};

const resolveLocationIdentifier = (row = {}) => {
  const candidate =
    row.locationId ??
    row.location?.id ??
    row.room?.locationId ??
    row.room?.location?.id ??
    null;

  if (candidate === null || candidate === undefined) {
    return '';
  }

  return String(candidate);
};

const resolveCityFromRow = (row = {}) =>
  row.city ||
  row.room?.city ||
  row.locationCity ||
  row.location?.city ||
  row.room?.locationCity ||
  '';

const getSessionGroupingKey = (row) => {
  if (!row) {
    return null;
  }

  const roomToken =
    resolveRoomIdentifier(row) ||
    resolveLocationIdentifier(row) ||
    resolveCityFromRow(row) ||
    'room';
  const dateToken = row.date || 'date';
  const sessionToken = row.sessionLabel || row.timeSlot || row.slotLabel || 'session';
  const timeToken = row.timeSlot || row.sessionLabel || 'time';

  return [roomToken, dateToken, sessionToken, timeToken].join('|');
};

const pickEarliestRow = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  return rows.reduce((earliest, candidate) => {
    if (!candidate) {
      return earliest;
    }
    if (!earliest) {
      return candidate;
    }

    const earliestDate = buildSessionDate(earliest.date, earliest.timeSlot);
    const candidateDate = buildSessionDate(candidate.date, candidate.timeSlot);

    if (!earliestDate && candidateDate) {
      return candidate;
    }
    if (!candidateDate) {
      return earliest;
    }
    return candidateDate < earliestDate ? candidate : earliest;
  }, null);
};

const aggregateSessionsForGroup = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const sessionsByKey = rows.reduce((acc, row) => {
    const sessionKey = getSessionGroupingKey(row);
    if (!sessionKey) {
      return acc;
    }
    if (!acc[sessionKey]) {
      acc[sessionKey] = [];
    }
    acc[sessionKey].push(row);
    return acc;
  }, {});

  return Object.values(sessionsByKey).map((sessionRows) => {
    const { totalRemaining, totalCapacity } = summarizeSessionCapacity(sessionRows);
    const seedRow = pickEarliestRow(sessionRows) || sessionRows[0] || null;
    const isBlocked = sessionRows.every((row) => row?.isBlocked);

    return {
      seedRow,
      totalRemaining,
      totalCapacity,
      isBlocked,
    };
  });
};

const PLUTO_LOGO_PATH = `${process.env.PUBLIC_URL || ''}/PlutoTV_Logo_1080x1080_RGB_Plutonium.ai.png`;

const rowsFromResponse = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  return [];
};

const getLocationDisplayName = (locationName, city = '') => {
  const safeName = typeof locationName === 'string' ? locationName.trim() : '';
  if (safeName) {
    return safeName;
  }
  const safeCity = typeof city === 'string' ? city.trim() : '';
  return safeCity || 'Upcoming Location';
};

const normalizeTimeSlot = (timeSlot) => {
  if (!timeSlot) {
    return '00:00:00';
  }
  return timeSlot.length === 5 ? `${timeSlot}:00` : timeSlot;
};

const parseDateParts = (dateString = '') => {
  if (typeof dateString !== 'string') {
    return null;
  }
  const [yearStr, monthStr, dayStr] = dateString.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);
  if ([year, monthIndex, day].some((value) => Number.isNaN(value))) {
    return null;
  }
  return { year, monthIndex, day };
};

const parseTimeParts = (timeString = '') => {
  if (typeof timeString !== 'string') {
    return null;
  }
  const normalized = normalizeTimeSlot(timeString);
  const [hourStr = '0', minuteStr = '0', secondStr = '0'] = normalized.split(':');
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  const second = Number(secondStr);
  if ([hour, minute, second].some((value) => Number.isNaN(value))) {
    return null;
  }
  return { hour, minute, second };
};

const buildSessionDate = (date, timeSlot) => {
  if (!date || !timeSlot) {
    return null;
  }
  const dateParts = parseDateParts(date);
  const timeParts = parseTimeParts(timeSlot);
  if (!dateParts || !timeParts) {
    return null;
  }
  const utcTimestamp = Date.UTC(
    dateParts.year,
    dateParts.monthIndex,
    dateParts.day,
    timeParts.hour,
    timeParts.minute,
    timeParts.second
  );
  const candidate = new Date(utcTimestamp);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};

const formatDateLabel = (date) => {
  const dateParts = parseDateParts(date);
  if (!dateParts) {
    return date;
  }
  const dateForDisplay = new Date(
    Date.UTC(dateParts.year, dateParts.monthIndex, dateParts.day, 12, 0, 0)
  );
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(dateForDisplay);
  } catch {
    return date;
  }
};

const formatTimeWithMeridiem = (timeSlot) => {
  const timeParts = parseTimeParts(timeSlot);
  if (!timeParts) {
    return timeSlot || 'Scheduling soon';
  }
  const period = timeParts.hour >= 12 ? 'PM' : 'AM';
  const displayHour = timeParts.hour % 12 === 0 ? 12 : timeParts.hour % 12;
  const displayMinute = String(timeParts.minute).padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
};

const formatSessionWindow = ({ date, timeSlot }) => {
  if (!date || !timeSlot) {
    return 'Scheduling soon';
  }

  const formattedDate = formatDateLabel(date);
  const formattedTime = formatTimeWithMeridiem(timeSlot);

  return `${formattedDate} • ${formattedTime}`;
};

const transformAvailabilityToEvents = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const rowsByCity = rows.reduce((acc, row) => {
    const rawCity = resolveCityFromRow(row) || 'City';
    const cityKey = rawCity.trim() || 'City';
    if (!acc[cityKey]) {
      acc[cityKey] = [];
    }
    acc[cityKey].push(row);
    return acc;
  }, {});

  return Object.entries(rowsByCity)
    .map(([cityKey, cityRows]) => {
      const sessionSummaries = aggregateSessionsForGroup(cityRows);
      if (sessionSummaries.length === 0) {
        return null;
      }

      const sortedSessions = [...sessionSummaries].sort((a, b) => {
        const aDate = buildSessionDate(a.seedRow?.date, a.seedRow?.timeSlot);
        const bDate = buildSessionDate(b.seedRow?.date, b.seedRow?.timeSlot);
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return aDate - bDate;
      });

      const nextSession =
        sortedSessions.find(
          (session) => !session.isBlocked && session.totalRemaining > 0
        ) ||
        sortedSessions.find((session) => !session.isBlocked) ||
        sortedSessions[0];

      if (!nextSession || !nextSession.seedRow) {
        return null;
      }

      const sampleRow = nextSession.seedRow;
      const normalizedCityName = cityKey || resolveCityFromRow(sampleRow) || 'City';
      const displayLocationName = getLocationDisplayName(
        sampleRow.locationName || sampleRow.room?.locationName,
        normalizedCityName
      );
      const locationAddress =
        sampleRow.room?.locationAddress ||
        sampleRow.locationAddress ||
        sampleRow.room?.location?.address ||
        '';

      const { totalRemaining, totalCapacity } = sessionSummaries.reduce(
        (acc, session) => {
          acc.totalRemaining += session.totalRemaining;
          acc.totalCapacity += session.totalCapacity;
          return acc;
        },
        { totalRemaining: 0, totalCapacity: 0 }
      );

      const normalizedTotalCapacity =
        totalCapacity > 0
          ? Math.max(totalCapacity, totalRemaining)
          : totalRemaining || sessionSummaries.length;

      const startsAt = buildSessionDate(sampleRow.date, sampleRow.timeSlot);

      const uniqueRooms = new Set(
        cityRows
          .map((row) => resolveRoomIdentifier(row) || resolveLocationIdentifier(row))
          .filter(Boolean)
      ).size;

      const liveRoomsCount = uniqueRooms || 1;
      const highlightNote = sampleRow.specialNote
        ? sampleRow.specialNote
        : `${liveRoomsCount} room${liveRoomsCount === 1 ? '' : 's'} • ${
            sessionSummaries.length
          } live session${sessionSummaries.length === 1 ? '' : 's'}`;

      return {
        id: `city-${normalizedCityName.replace(/\s+/g, '-').toLowerCase()}`,
        location: displayLocationName,
        locationAddress,
        city: normalizedCityName,
        sessionName:
          sampleRow.sessionLabel ||
          (sampleRow.sessionNumber ? `Session ${sampleRow.sessionNumber}` : 'Upcoming Session'),
        timeDetail: formatSessionWindow(sampleRow),
        status:
          totalRemaining > 0 && normalizedTotalCapacity > 0
            ? `${totalRemaining} slots remaining`
            : totalRemaining > 0
            ? `${totalRemaining} slots remaining`
            : 'Fully booked',
        highlight: highlightNote,
        startsAt,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (!a.startsAt && !b.startsAt) return 0;
      if (!a.startsAt) return 1;
      if (!b.startsAt) return -1;
      return a.startsAt - b.startsAt;
    })
    .map(({ startsAt, ...event }) => event);
};

const LandingPage = () => {
  const { rageRoom } = useApi();
  const locationFilter = (process.env.REACT_APP_DEFAULT_LOCATION_ID || '').trim();

  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [availabilityError, setAvailabilityError] = useState(null);

  const fetchAvailability = useCallback(async () => {
    if (!rageRoom?.public?.listAvailability) {
      setAvailabilityError('Availability service is not configured.');
      setAvailabilityRows([]);
      setLoadingAvailability(false);
      return;
    }

    setLoadingAvailability(true);
    setAvailabilityError(null);

    try {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      const rangeEnd = new Date(today);
      rangeEnd.setDate(rangeEnd.getDate() + 14);
      const endDate = rangeEnd.toISOString().split('T')[0];

      const availabilityParams = {
        startDate,
        endDate,
        onlyAvailable: false,
      };

      if (locationFilter) {
        availabilityParams.locationId = locationFilter;
      }

      const response = await rageRoom.public.listAvailability(availabilityParams);
      setAvailabilityRows(rowsFromResponse(response));
    } catch (error) {
      setAvailabilityError(error.message || 'Unable to load live availability right now.');
      setAvailabilityRows([]);
    } finally {
      setLoadingAvailability(false);
    }
  }, [locationFilter]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const upcomingEvents = useMemo(
    () => transformAvailabilityToEvents(availabilityRows),
    [availabilityRows]
  );

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 10, md: 14 },
        backgroundColor: '#000',
        backgroundImage: 'none',
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={8}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={6}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box flex={1}>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Box
                  component="img"
                  src={PLUTO_LOGO_PATH}
                  alt="Pluto TV logo"
                  sx={{
                    height: { xs: 64, sm: 80, md: 120 },
                    width: 'auto',
                    mb: 3,
                    filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.6))',
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                component="h3"
                color="secondary"
                gutterBottom
                sx={{ marginBottom: '0px', textAlign: 'center' }}
              >
                The holidays are brutal.
              </Typography>
              <Typography
                variant="h4"
                component="h3"
                color="secondary"
                gutterBottom
                sx={{ textAlign: 'center' }}
              >
                Free rage rooms can help.
              </Typography>
              <Typography variant="h6" component="h6" color="primary" sx={{ textAlign: 'center', mb: 2 }}>
                Holiday Rage Room Takeover
                <br />
                Thursday Dec. 11
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 'normal', textAlign: 'center', mb: 1.5 }}
              >
                New York | The Ragery
                <br />
                Los Angeles | Rage Ground
                <br />
                Atlanta | Total Rage ATL
                <br />
                Chicago | The Angry Cactus Lounge
                <br />
                Washington D.C. | Kraken Penn Quarter
                <br />
                Houston | Break Life
                <br />
                Dallas–Ft. Worth | The Secret Chambers
                <br />
                Philadelphia | Rage Philly
                <br />
                Phoenix | Breakthrough Smash Room
                <br />
                Raleigh–Durham | Wreck It Rage Room
                <br />
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ mb: 4, textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.1em' }}
              >
                BOOK A RAGE ROOM
                <br />
                COURTESY OF PLUTO TV
              </Typography>
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                sx={{ justifyContent: 'center' }}
                spacing={2}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  component={RouterLink}
                  to="/book"
                >
                  Book a Session
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  component={RouterLink}
                  to="/book"
                >
                  View Availability
                </Button>
              </Stack>
            </Box>
            <Card
              sx={{
                flexBasis: { md: '40%' },
                minWidth: { xs: '100%', md: 360 },
                maxHeight: 460,
              }}
            >
              <CardContent>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: '0.2em' }}
                >
                  UPCOMING EVENTS
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    maxHeight: 360,
                    overflowY: 'auto',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(244,243,248,0.05)',
                      borderRadius: 999,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(255,242,0,0.45)',
                      borderRadius: 999,
                    },
                  }}
                >
                  {loadingAvailability ? (
                    <Typography color="text.secondary">Loading live availability…</Typography>
                  ) : availabilityError ? (
                    <Typography color="error">{availabilityError}</Typography>
                  ) : upcomingEvents.length === 0 ? (
                    <Typography color="text.secondary">
                      No live sessions are available right now. Check back soon.
                    </Typography>
                  ) : (
                    <Stack spacing={3}>
                      {upcomingEvents.map((event, index) => {
                        const isFullyBookedWaitlist =
                          typeof event.status === 'string' &&
                          event.status.toLowerCase().includes('fully booked');

                        return (
                          <Box key={event.id}>
                            <Typography variant="h5">{event.location}</Typography>
                            {event.city && (
                              <Typography color="text.secondary">{event.city}</Typography>
                            )}
                            <Typography color="text.secondary">{event.timeDetail}</Typography>
                            <Typography color="text.secondary">{event.locationAddress}</Typography>
                            <Typography sx={{ mt: 1, fontStyle: 'italic' }}>
                              {event.highlight}
                            </Typography>
                            <Chip label={event.status} size="small" color="primary" sx={{ mt: 1 }} />
                            {isFullyBookedWaitlist && (
                              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                                Fully booked? Select "Book A Session" to join the waitlist for this
                                location.
                              </Typography>
                            )}
                            {index < upcomingEvents.length - 1 && (
                              <Divider sx={{ mt: 3, borderColor: 'rgba(244,243,248,0.08)' }} />
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Stack>

          <Footer />
        </Stack>
      </Container>
    </Box>
  );
};

export default LandingPage;